<template>
	<div class="dorm-scroll-panel">
		<div class="panel-title">{{ titleText }}</div>
		<div class="panel-main" v-if="!loading && displayRows.length">
			<table class="panel-table head-table">
				<thead>
					<tr>
						<th v-for="(h, i) in headers" :key="'h-' + i">{{ h }}</th>
					</tr>
				</thead>
			</table>
			<div class="panel-body" ref="body">
				<div class="scroll-track" ref="track">
					<div class="scroll-block" ref="blockA">
						<table class="panel-table">
							<tbody>
								<tr v-for="(row, idx) in displayRows" :key="'a-' + idx">
									<td
										v-for="(cell, cIdx) in row.cells"
										:key="cIdx"
										:title="row.tip"
										:style="cell.style || null"
									>{{ cell.text }}</td>
								</tr>
							</tbody>
						</table>
						<!-- 结尾空白，循环回开头时有间隔 -->
						<div class="scroll-gap"></div>
					</div>
					<div class="scroll-block" aria-hidden="true">
						<table class="panel-table">
							<tbody>
								<tr v-for="(row, idx) in displayRows" :key="'b-' + idx">
									<td
										v-for="(cell, cIdx) in row.cells"
										:key="cIdx"
										:style="cell.style || null"
									>{{ cell.text }}</td>
								</tr>
							</tbody>
						</table>
						<div class="scroll-gap"></div>
					</div>
				</div>
			</div>
		</div>
		<div class="panel-empty" v-else-if="!loading">暂无数据</div>
		<div class="panel-empty" v-else>加载中...</div>
	</div>
</template>

<script>
import { formateDate } from './dormScreenFormat'

export default {
	name: 'DormScrollPanel',
	props: {
		/** attention | leave | late | access */
		panelType: { type: String, required: true },
		list: { type: Array, default: null },
		/** morning | evening；late 面板按 Tab 只显示晚离或晚归 */
		attendanceTab: { type: String, default: '' },
	},
	data() {
		return {
			rafId: null,
			/** 上次列表指纹：相同则不重启滚动，减少 TV 上无意义重绘 */
			rowSig: '',
			/** 高铁站式慢速：约 28px/秒，方便看清姓名 */
			scrollSpeedPx: 0.028,
		}
	},
	computed: {
		loading() {
			return this.list == null
		},
		rows() {
			const source = Array.isArray(this.list) ? this.list : []
			if (this.panelType !== 'late') return source
			const want = this.attendanceTab === 'evening'
				? '晚归'
				: this.attendanceTab === 'morning'
					? '晚离'
					: ''
			if (!want) return source
			return source.filter((item) => this.normalizeLateType(item) === want)
		},
		headers() {
			if (this.panelType === 'leave') return ['姓名', '宿舍号', '请假时间']
			if (this.panelType === 'late') return ['姓名', '班级', '宿舍号', '类型']
			if (this.panelType === 'access') return ['姓名', '班级', '宿舍号', '时间']
			return ['姓名', '班级', '宿舍号', '结果']
		},
		titleText() {
			if (this.panelType === 'attention') return `考勤关注(${this.rows.length}人)`
			if (this.panelType === 'leave') return `请假学生(${this.uniqueLeaveCount}人)`
			if (this.panelType === 'late') {
				const lateTitle = this.attendanceTab === 'evening'
					? '晚归'
					: this.attendanceTab === 'morning'
						? '晚离'
						: '晚离/晚归'
				return `${lateTitle}(${this.rows.length}人)`
			}
			return '最新门禁流水'
		},
		uniqueLeaveCount() {
			const seen = new Set()
			this.rows.forEach((item) => {
				seen.add(item.UserId || item.studentId || item.id || item.stuName || item.name)
			})
			return seen.size
		},
		displayRows() {
			const limit = this.panelType === 'access' ? 60 : 40
			// 先排序再截取，避免截到乱序前 N 条
			return this.sortedSourceRows().slice(0, limit).map((item) => this.mapRow(item))
		},
	},
	watch: {
		displayRows() {
			this.$nextTick(() => this.restartAutoScroll(false))
		},
	},
	mounted() {
		this.$nextTick(() => this.restartAutoScroll(true))
	},
	beforeDestroy() {
		this.stopAutoScroll()
	},
	methods: {
		/** 解析行时间，供排序；仅时分秒时按当天相对秒数比较 */
		rowTimeMs(item) {
			const raw =
				item.time ||
				item.LastRegTime ||
				item.lastRegTime ||
				item.passTime ||
				item.startTime ||
				item.endTime ||
				''
			if (raw === '' || raw == null) return 0
			if (typeof raw === 'number') return raw
			const s = String(raw).trim()
			if (/^\d{1,2}:\d{1,2}(:\d{1,2})?$/.test(s)) {
				const parts = s.split(':').map((n) => Number(n) || 0)
				return (parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0)) * 1000
			}
			const d = new Date(s.replace(/-/g, '/').replace(/T/g, ' '))
			return isNaN(d.getTime()) ? 0 : d.getTime()
		},
		/** 门禁/请假按时间倒序；其余保持接口顺序 */
		sortedSourceRows() {
			const list = this.rows.slice()
			if (this.panelType === 'access' || this.panelType === 'leave') {
				list.sort((a, b) => this.rowTimeMs(b) - this.rowTimeMs(a))
			}
			return list
		},
		rowsFingerprint() {
			return this.displayRows
				.map((row) => (row.cells || []).map((c) => c.text).join('\t'))
				.join('\n')
		},
		stopAutoScroll() {
			if (this.rafId != null) {
				cancelAnimationFrame(this.rafId)
				this.rafId = null
			}
		},
		/**
		 * @param {boolean} force 强制重启（挂载时）
		 */
		restartAutoScroll(force) {
			const sig = this.rowsFingerprint()
			if (!force && sig === this.rowSig && this.rafId != null) return
			this.rowSig = sig

			this.stopAutoScroll()
			const body = this.$refs.body
			const track = this.$refs.track
			const blockA = this.$refs.blockA
			if (!track || !blockA || !this.displayRows.length) return

			// 结尾→下一轮开头：空白约等于可视区一半，慢速下也能看清间隔
			const gapH = Math.max(96, Math.floor((body && body.clientHeight ? body.clientHeight : 160) * 0.5))
			const gaps = track.querySelectorAll('.scroll-gap')
			for (let i = 0; i < gaps.length; i++) {
				gaps[i].style.height = gapH + 'px'
			}

			// 用 transform 亚像素位移，避免 scrollTop 整数跳变造成「一格一格」的失真感
			let offset = 0
			track.style.transform = 'translate3d(0,0,0)'
			const loopHeight = () => Math.max(1, blockA.offsetHeight)

			let last = performance.now()
			const tick = (now) => {
				const t = this.$refs.track
				const a = this.$refs.blockA
				if (!t || !a) {
					this.rafId = null
					return
				}
				const dt = Math.min(now - last, 40)
				last = now
				offset += this.scrollSpeedPx * dt
				const loopAt = loopHeight()
				if (offset >= loopAt) offset -= loopAt
				t.style.transform = `translate3d(0,${-offset}px,0)`
				this.rafId = requestAnimationFrame(tick)
			}
			this.rafId = requestAnimationFrame(tick)
		},
		/** 统一晚离/晚归类型文案。最后更新：2026-10-23 */
		normalizeLateType(item) {
			let typeText = (item && (item.type || item.resultName || item.resultTypeName)) || ''
			if (!typeText && item && item.result) {
				if (item.result === 'LATE_EXIT') typeText = '晚离'
				else if (item.result === 'LATE_RETURN') typeText = '晚归'
				else typeText = item.result
			}
			if (['晚出', '迟离', 'LATE_EXIT'].includes(typeText)) typeText = '晚离'
			else if (['迟归', 'LATE_RETURN'].includes(typeText)) typeText = '晚归'
			return typeText
		},
		mapRow(item) {
			const tip = Object.keys(item || {}).map((k) => item[k]).join(' ')
			const name = item.stuName || item.name || ''
			const cls = item.deptName || item.className || ''
			const dorm = item.dormName || item.roomName || ''
			if (this.panelType === 'leave') {
				let timeText = '全天'
				if (item.startTime && item.endTime) {
					timeText =
						formateDate('dd/HH:MM', item.startTime) +
						'~' +
						formateDate('dd/HH:MM', item.endTime)
				} else if (item.startTime) {
					timeText = formateDate('dd/HH:MM', item.startTime)
				} else if (item.time || item.leaveTime) {
					timeText = formateDate('dd/HH:MM', item.time || item.leaveTime)
				}
				return {
					tip,
					cells: [{ text: name }, { text: dorm }, { text: timeText }],
				}
			}
			if (this.panelType === 'late') {
				const typeText = this.normalizeLateType(item)
				return {
					tip,
					cells: [
						{ text: name },
						{ text: cls },
						{ text: dorm },
						{ text: typeText, style: { color: '#ff7675' } },
					],
				}
			}
			if (this.panelType === 'access') {
				const timeVal = item.time || item.LastRegTime || item.lastRegTime || item.passTime || ''
				return {
					tip,
					cells: [
						{ text: name },
						{ text: cls },
						{ text: dorm },
						{ text: timeVal ? formateDate('HH:MM:SS', timeVal) : '' },
					],
				}
			}
			let resultText =
				item.resultName || item.resultTypeName || item.result || item.status || '需关注'
			const map = {
				NOT_EXITED: '未离寝',
				LATE_EXIT: '晚离',
				NOT_RETURNED: '未归寝',
				LATE_RETURN: '晚归',
				UNJUDGED: '未判定',
				ISSUE: '异常',
				有问题: '异常',
				DONE: '正常',
				已完成: '正常',
				离寝归寝均正常: '正常',
			}
			if (map[resultText]) resultText = map[resultText]
			const color = resultText === '未判定' ? '#f6c343' : '#ff7675'
			return {
				tip,
				cells: [
					{ text: name },
					{ text: cls },
					{ text: dorm },
					{ text: resultText, style: { color } },
				],
			}
		},
	},
}
</script>

<style scoped>
.dorm-scroll-panel {
	width: 100%;
	height: 100%;
	box-sizing: border-box;
	border: 1px solid rgba(45, 118, 252, 0.45);
	background: rgba(8, 18, 48, 0.55);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.panel-title {
	flex: 0 0 auto;
	height: 2.1vw;
	line-height: 2.1vw;
	padding: 0 0.6vw;
	font-size: 0.9vw;
	color: #fff;
	background: rgba(30, 53, 193, 0.18);
}
.panel-main {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	padding: 0 0.4vw 0.4vw;
}
.head-table {
	flex: 0 0 auto;
}
.panel-body {
	flex: 1 1 auto;
	min-height: 0;
	overflow: hidden;
}
.scroll-track {
	width: 100%;
	will-change: transform;
	backface-visibility: hidden;
}
.scroll-block {
	width: 100%;
}
/* 列表滚完到下一次开头之间的空白（高度由 JS 按可视区一半设置） */
.scroll-gap {
	width: 100%;
	height: 8vw;
	min-height: 96px;
	flex-shrink: 0;
}
.panel-table {
	width: 100%;
	border-collapse: collapse;
	table-layout: fixed;
}
.panel-table th,
.panel-table td {
	padding: 0.35vw 0.2vw;
	font-size: 0.72vw;
	text-align: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: #e2e8f0;
}
.panel-table th {
	color: #1c97fe;
	font-weight: 600;
}
.panel-empty {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #94a3b8;
	font-size: 1vw;
}
</style>
